import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePendingImport } from "@/app/hostEvents";
import { FileUp, Linkedin, CalendarDays } from "lucide-react";
import type { ImportProgress } from "@hub/shared";
import { Button, Field, Input, Pill, Select, toast } from "@/ui";
import { useEngineMutation } from "@/engine/hooks";
import { pickTextFile } from "./download";
import s from "../shared.module.css";

const TARGETS = [
  ["skip", "Skip"], ["displayName", "Full name"], ["firstName", "First name"], ["lastName", "Last name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"], ["title", "Title"], ["location", "Location"], ["linkedin", "LinkedIn URL"], ["birthday", "Birthday"], ["notes", "Notes"],
] as const;

/**
 * CSV import with column mapping and a preview of the first rows. LinkedIn exports map automatically.
 * After an import the toast offers to record what you need and offer (the matching engine needs both); pass
 * `onImported` to take over that follow-through, as onboarding does.
 */
export function ImportPanel({ onImported }: { onImported?: (result: ImportProgress) => void } = {}) {
  const navigate = useNavigate();
  const [file, setFile] = useState<{ name: string; content: string; kind: "csv" | "linkedin" } | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [groupName, setGroupName] = useState("");
  const [result, setResult] = useState<ImportProgress | null>(null);
  const preview = useEngineMutation("previewCsv", { invalidate: false });
  const importCsv = useEngineMutation("importCsv");

  const load = async (f: { name: string; content: string }, kind: "csv" | "linkedin") => {
    // LinkedIn's Connections.csv starts with a few lines of notes before the header.
    const content = kind === "linkedin" ? f.content.replace(/^[\s\S]*?(?=First Name,)/, "") : f.content;
    setFile({ name: f.name, content, kind });
    setResult(null);
    setGroupName(kind === "linkedin" ? "LinkedIn connections" : "");
    const p = await preview.mutateAsync({ csv: content });
    const m = { ...p.suggestedMapping };
    if (kind === "linkedin") {
      for (const h of p.headers) {
        const k = h.toLowerCase();
        if (k === "first name") m[h] = "firstName";
        else if (k === "last name") m[h] = "lastName";
        else if (k === "url") m[h] = "linkedin";
        else if (k === "email address") m[h] = "email";
        else if (k === "company") m[h] = "company";
        else if (k === "position") m[h] = "title";
        else m[h] = "skip";
      }
    }
    setMapping(m);
  };

  const pick = async (kind: "csv" | "linkedin") => {
    const f = await pickTextFile(["csv"]);
    if (f) await load(f, kind);
  };

  // A file picked through the desktop host (Settings > Integrations, or the native menu) lands here.
  const pending = usePendingImport((st) => st.file);
  const clearPending = usePendingImport((st) => st.clear);
  useEffect(() => {
    if (!pending) return;
    clearPending();
    void load({ name: pending.name, content: pending.content }, pending.kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const run = async () => {
    if (!file) return;
    const r = await importCsv.mutateAsync({ csv: file.content, mapping, groupName: groupName.trim() || null });
    setResult(r);
    setFile(null);
    if (onImported) {
      onImported(r);
      return;
    }
    toast(`Imported ${r.created} people. Hub matches their needs with offers once it knows yours.`, "success", [
      { label: "Add what you need", onClick: () => navigate("/needs?new=1") },
      { label: "Add what you offer", onClick: () => navigate("/services?new=1") },
    ]);
  };

  const p = preview.data;
  return (
    <section className={s.section} style={{ marginTop: 20 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Import</div>
      <div className="u-row" style={{ gap: 8, flexWrap: "wrap" }}>
        <Button icon={<FileUp size={14} />} onClick={() => pick("csv")}>CSV file</Button>
        <Button icon={<Linkedin size={14} />} onClick={() => pick("linkedin")}>LinkedIn connections</Button>
        <Button icon={<CalendarDays size={14} />} variant="outline" onClick={() => toast("Calendar file import arrives with the desktop app")} title="Not available yet">Calendar (ICS)</Button>
      </div>
      {result && <div className="u-small" style={{ marginTop: 8 }}>Last import: {result.created} created, {result.updated} updated.</div>}
      {file && p && (
        <div style={{ marginTop: 12 }} className="u-col">
          <div className="u-row"><Pill>{file.name}</Pill><span className="u-small u-muted">{p.headers.length} columns. Map each column to a field, then import.</span></div>
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>{p.headers.map((h) => <th key={h}>
                  <div>{h}</div>
                  <Select value={mapping[h] ?? "skip"} onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))} style={{ height: 26, marginTop: 4, minWidth: 120 }} aria-label={`Map ${h}`}>
                    {TARGETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </Select>
                </th>)}</tr>
              </thead>
              <tbody>
                {p.rows.map((r, i) => <tr key={i}>{p.headers.map((_, j) => <td key={j}>{r[j]}</td>)}</tr>)}
              </tbody>
            </table>
          </div>
          <div className="u-row" style={{ justifyContent: "space-between" }}>
            <Field label="Add everyone to a group (optional)"><Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Conference 2026" style={{ width: 220 }} /></Field>
            <span className="u-row">
              <Button variant="ghost" onClick={() => setFile(null)}>Cancel</Button>
              <Button variant="primary" loading={importCsv.isPending} onClick={run} disabled={!Object.values(mapping).some((v) => v !== "skip")}>Import</Button>
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
